// Deserializer: backend Workflow → canvas WorkflowDoc. The inverse of serialize.ts, used
// to reopen any workflow the server holds, unless this browser has a newer unsynced copy.
// When builderJson is absent it is lossy: the engine stores only executable fields, so genuinely
// UI-only data (custom labels, the pipeline add/move choice) is reconstructed with defaults. Anything the engine
// DOES store has to be read back, even when a default looks harmless: the canvas is saved from what
// this file returns, so a default written over a stored value is not lossy, it is a silent rewrite
// of the running workflow.
//
// Observed backend normalization (see the create smoke test): triggerJson/nodesJson/edgesJson come
// back as JSON *strings*; node `type` is snake_case; per-node config is nested under `data`; the
// trigger and exit nodes aren't stored (the trigger is the triggerJson; a run just ends at a leaf).
import type { Edge } from '@xyflow/react'
import { ROLE_ASSIGNEE_PREFIX } from 'lib/taskAssignee'
import type { Workflow } from 'app/generated/workflows'
import { logError } from 'lib/logging'
import { ObjectId } from 'lib/ObjectId'
import { type Condition, type ConditionScope, type FieldOperator, operatorNeedsValue } from './conditions'
import { CONTACT_TAGS_FIELD, isDaysAgoFieldKey, PAPERWORK_STATUS_FIELD } from './dataContext'
import { upgradeStoredNodes } from './migrate'
import { DEFAULT_PAPERWORK_EXPIRY_DAYS } from './paperworkStep'
import {
  CONTACT_TAGS_ATTRIBUTE,
  isDateAnchored,
  isManualEnrollmentTrigger,
  MESSAGE_ENGINE_ATTRIBUTE,
  stepStageAttribute,
} from './serialize'
import type { ListCopy } from 'lib/listCopy'
import { kindForEngineEvent, type TriggerKind, triggerLabel, triggerObject } from './triggerCatalog'
import { treatmentFromFilters } from './treatmentTrigger'
import type {
  DateOffsetUnit,
  ReenrollmentPolicy,
  ReplyOption,
  WaitUnit,
  WorkflowNode,
  WorkflowNodeKind,
  WorkflowSettings,
} from 'app/types/workflows'
import { DEFAULT_WORKFLOW_SETTINGS } from './workflowSettings'
import type { WorkflowDoc } from './workflowStore'

type EngineNode = { id: string; type: string; position?: { x: number; y: number }; data?: Record<string, unknown> }
type EngineEdge = { id: string; source: string; sourceHandle?: string; target: string; targetHandle?: string }
type EngineTrigger = {
  event?: string
  filters?: { attribute: string; operator: string; value?: string }[]
  stateFilters?: { attribute: string; operator: string; value?: string }[]
  reenrollment?: string
  filterLogic?: string
  stateFilterLogic?: string
  requireContactMatch?: boolean
}

/** JSON scalar fields arrive as strings from this backend; tolerate objects too. */
export function parseJsonField<T>(value: unknown): T | undefined {
  if (value == null) return undefined
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T
    } catch (error) {
      logError(`Failed to parse workflow JSON field`, error)
      return undefined
    }
  }
  return value as T
}

const KIND_FROM_ENGINE: Record<string, WorkflowNodeKind> = {
  send_sms: 'sendSms',
  initiate_agent: 'aiAgentSms',
  initiate_call: 'initiateCall',
  create_task: 'createTask',
  edit_task: 'editTask',
  add_tags: 'tagObject',
  remove_tags: 'tagObject',
  set_pipeline_stage: 'addToPipeline',
  assign_owner: 'assignOwner',
  create_note: 'addNote',
  send_paperwork: 'sendPaperwork',
  send_notification: 'sendNotification',
  edit_contact: 'editObject',
  branch: 'branch',
  wait: 'wait',
  wait_for_reply: 'waitForReply',
  wait_for_call_result: 'waitForCallResult',
  confirm_appointment: 'updateAppointment',
}

/** One entry of a `branch` node's stored `conditions` array. */
type BranchCondition = {
  id?: string
  /** The record the group reads. Absent means the contact. */
  source?: string
  /** `value` is not always a string: a filter on a boolean field stores a real boolean, because the
   *  engine compares step filters by identity. See serialize.toStepValue. */
  filters?: { attribute: string; operator: string; value?: unknown }[]
  filterLogic?: string
}

/** Which canvas kind an engine node reopens as, from an ALREADY LOWER-CASED engine type (the caller
 *  normalizes once and passes the same value on to `nodeDataFromEngine`). The engine has a single
 *  `branch` type for both of the builder's condition blocks, and the stored condition id is the
 *  only thing telling them apart: serialize writes 'pass' for a Filter's single gate and 'yes' for
 *  a Branch's fork. Read from the type alone, every Filter came back as a two-way Branch with a
 *  live "No" port. */
function kindFromEngineNode(engineType: string, data: Record<string, unknown>): WorkflowNodeKind {
  if (engineType === 'branch' && (parseJsonField<BranchCondition[]>(data.conditions) ?? [])[0]?.id === 'pass') {
    return 'filter'
  }
  // A Wait carrying early-exit conditions is the builder's Wait until block (TRK-6036).
  if (engineType === 'wait' && parseJsonField<{ filters?: unknown[] }>(data.earlyExit)?.filters?.length) {
    return 'waitUntil'
  }
  if (engineType === 'wait_for_reply' && parseJsonField<unknown[]>(data.replyOptions)?.length) return 'waitForKeyword'
  return KIND_FROM_ENGINE[engineType] ?? 'wait'
}

/** An engine event (+ its legacy type filter) to the nearest user trigger kind. The event-to-kind
 *  mapping itself is the catalog's (`kindForEngineEvent`); this only handles what the catalog cannot
 *  express: events retired by the event-stream pivot, and the legacy activity docs where the `type`
 *  filter, not the event, decided which trigger it was. */
function kindFromEngine(event: string | undefined, typeFilter: string | undefined): TriggerKind {
  // The backend stores the event lower-cased, but the GraphQL enum we send is upper (CONTACT_CREATED).
  // Match either: a doc read straight back from a serialize result must not fall through to the
  // default, which is one of the two ways a date trigger got rewritten to contact_created on save.
  const normalized = event?.toLowerCase() ?? ''
  switch (normalized) {
    // Legacy pre-pivot value; the server migration rewrote these to form_created, but tolerate it.
    case 'form_submitted':
      return 'form_created'
    // Legacy activity docs: a type=Call filter meant the Call trigger; type=Form meant the form preset.
    case 'activity_created':
      if (typeFilter === 'Call') return 'call_created'
      if (typeFilter === 'Form') return 'form_created'
      break
    case 'activity_updated':
      if (typeFilter === 'Call') return 'call_updated'
      break
  }
  return kindForEngineEvent(normalized) ?? 'contact_created'
}

/** Whether a reconstructed kind keeps the raw activity-type filter as user-editable data (the generic
 *  activity triggers) vs baking it into the kind (appointment/call presets). */
const GENERIC_ACTIVITY_KINDS = new Set<TriggerKind>(['activity_created', 'activity_updated'])

// Raw Message field → our aliased field key (inverse of serialize's boundary mapping).
const MESSAGE_FIELD_FROM_ENGINE: Record<string, string> = Object.fromEntries(
  Object.entries(MESSAGE_ENGINE_ATTRIBUTE).map(([alias, raw]) => [raw, alias]),
)

// Engine reenrollment values are already lowercase and match ours 1:1; guard unknowns to 'ignore'.
const REENROLLMENT_FROM_ENGINE: Record<string, ReenrollmentPolicy> = {
  ignore: 'ignore',
  restart: 'restart',
  allow_multiple: 'allow_multiple',
}

/** "2h" → { duration: 2, unit: 'hours' }. Defaults to 1 hour when unparseable. */
export function decodeDuration(value?: string): { duration: number; unit: WaitUnit } {
  const match = /^(\d+)\s*([mhd])$/.exec(value?.trim() ?? '')
  if (!match) return { duration: 1, unit: 'hours' }
  const unit: WaitUnit = match[2] === 'm' ? 'minutes' : match[2] === 'd' ? 'days' : 'hours'
  return { duration: Number(match[1]), unit }
}

/** The engine's `date_reached` operator. Its filters carry the schedule of a date-anchored trigger,
 *  not a condition the user typed, so they never become condition rows. */
const DATE_REACHED_OPERATOR = 'date_reached'

/** "-3d" / "+1mo" → one canvas moment (inverse of serialize.encodeOffset). Undefined means the
 *  builder cannot represent this value, and the caller keeps the raw string rather than dropping it
 *  (see `dateScheduleFromFilters`).
 *
 *  Zero is rejected deliberately. The engine refuses a zero offset and `encodeOffset` clamps the
 *  amount to at least 1, so decoding "+0d" to a usable moment would silently reschedule it to "+1d"
 *  the next time the workflow was saved. That is the same silent rewrite this whole file is fixing,
 *  one field further down. */
export function decodeOffset(
  value?: string,
): { amount: number; unit: DateOffsetUnit; direction: 'before' | 'after' } | undefined {
  const match = /^([+-])(\d*[1-9]\d*)(mo|m|h|d|w|y)$/.exec(value?.trim() ?? '')
  if (!match) return undefined
  return {
    amount: Number(match[2]),
    unit: match[3] as DateOffsetUnit,
    direction: match[1] === '-' ? 'before' : 'after',
  }
}

/** The `date_reached` filters of a date-anchored trigger → the canvas schedule, splitting the values
 *  the builder can render from the ones it cannot.
 *
 *  Unrenderable values are RETURNED, not discarded. A workflow with one good moment and one bad one
 *  would otherwise lose the bad one the moment someone opened and saved it, with nothing on screen
 *  to say a send had gone missing. Kept, they ride back out through serialize untouched and raise a
 *  blocking issue on the trigger until a human repairs them. */
export function dateScheduleFromFilters(filters: { attribute: string; operator: string; value?: string }[]): {
  dateField?: string
  dateOffsets: { amount: number; unit: DateOffsetUnit; direction: 'before' | 'after' }[]
  invalidDateOffsets: string[]
} {
  const dateFilters = filters.filter((f) => f.operator === DATE_REACHED_OPERATOR)
  const dateOffsets: { amount: number; unit: DateOffsetUnit; direction: 'before' | 'after' }[] = []
  const invalidDateOffsets: string[] = []
  for (const filter of dateFilters) {
    const offset = decodeOffset(filter.value)
    if (offset) dateOffsets.push(offset)
    else invalidDateOffsets.push(filter.value ?? '')
  }
  return { dateField: dateFilters[0]?.attribute || undefined, dateOffsets, invalidDateOffsets }
}

// Engine filter operators → the builder's condition operators (reverse of serialize.FIELD_OP_MAP).
const ENGINE_OP_TO_FIELD: Record<string, FieldOperator> = {
  exactly: 'is',
  not_exactly: 'is_not',
  contains: 'contains',
  greater_than: 'greater_than',
  greater_equal_than: 'greater_equal_than',
  lesser_than: 'lesser_than',
  lesser_equal_than: 'lesser_equal_than',
  attribute_exists: 'is_set',
  attribute_not_exists: 'is_empty',
}

/** One engine filter → one builder condition row. Presence operators carry no user value in the
 *  builder, so the engine's `attribute_exists` sentinel (serialize.ATTRIBUTE_EXISTS_VALUE) is
 *  dropped here. Kept, it would prefill the value box with "true" the moment someone switched the
 *  row off "is set". Re-serializing puts the sentinel straight back. */
function conditionFromFilter(
  field: string,
  engineOperator: string,
  value?: unknown,
  scope?: ConditionScope,
): Condition {
  const operator = ENGINE_OP_TO_FIELD[engineOperator] ?? 'is'
  // A step filter on a boolean field stores a real boolean (see serialize.toStepValue), so a value
  // read back here is not always a string. Conditions hold strings; the value controls compare on
  // them, and a boolean arriving raw would blank the row instead of showing Yes.
  const text = value === undefined || value === null ? undefined : typeof value === 'string' ? value : String(value)
  return {
    type: 'field',
    field,
    operator,
    value: operatorNeedsValue(operator) ? text : undefined,
    ...(scope ? { scope } : {}),
  }
}

/** Canvas source-handle from the engine handle (inverse of serialize.encodeSourceHandle). */
export function decodeSourceHandle(sourceKind: WorkflowNodeKind | undefined, handle?: string): string | undefined {
  // A Filter card renders one unnamed source handle, so its engine 'pass' must decode to the
  // default port — keeping the id would point the edge at a handle that isn't on the node.
  if (sourceKind === 'filter') return undefined
  if (sourceKind === 'branch') return handle === 'default' ? 'no' : 'yes' // only 'default' is the No port
  if (!handle || handle === 'done') return undefined
  if (sourceKind === 'waitForReply') return handle === 'replied' ? 'yes' : 'no'
  if (sourceKind === 'waitUntil') return handle === 'conditions_met' ? 'yes' : 'no'
  return handle // answered | no_answer | voicemail | task
}

/** Reconstruct one canvas node's data from the engine node, filling UI-only fields with defaults. */
function nodeDataFromEngine(
  engineType: string,
  data: Record<string, unknown>,
  copy: ListCopy,
): Record<string, unknown> {
  const s = (v: unknown) => (typeof v === 'string' ? v : undefined)
  switch (engineType) {
    case 'send_sms': {
      // Preserve the stored recipient: hardcoding cellPhone here silently rewrote callerPhone
      // sends on engine-only docs. `formPhone` was the interim contract name for the form's
      // `phone` before api #566 settled on the plain key; map it for any doc from that window.
      const toField = s(data.toField)
      return {
        label: 'Send SMS',
        toField: toField === 'formPhone' ? 'phone' : (toField ?? 'cellPhone'),
        message: s(data.message) ?? '',
        // Engine name is quietHoursAction (api #587); absent means hold, same as the builder default.
        nightSend: s(data.quietHoursAction) === 'skip' ? 'skip' : 'hold',
      }
    }
    case 'initiate_agent': {
      // Any followUps the engine still holds are ignored: nudges are canvas structure now
      // (Wait for reply, nudge on the no-reply path), not a field on this node.
      return {
        label: 'Hand off to AI agent',
        toField: s(data.toField) ?? 'cellPhone',
        message: s(data.message) ?? '',
        brief: s(data.brief) ?? '',
        whenBusy: s(data.busyBehavior) === 'take_over' ? 'takeOver' : 'skip',
        skipIfAgentSpokeWithinDays: typeof data.busyLimitDays === 'number' ? data.busyLimitDays : undefined,
      }
    }
    case 'initiate_call':
      // whisperMessage is the contact-leg opener the engine actually stores; dropping it here
      // erased the greeting on the next save.
      return { label: 'Initiate call', ringNumber: s(data.ringNumber), whisper: s(data.whisperMessage) }
    case 'create_task': {
      // `dueDate` and `description` are both written by serialize, so read them back. Hard-coding
      // `relative` turned a task due on a fixed date into one with no due date at all, and dropping
      // the description lost the body on the next save.
      const dueDate = s(data.dueDate)
      return {
        label: 'Create task',
        title: s(data.title) ?? '',
        description: s(data.description),
        dueType: dueDate ? 'absolute' : 'relative',
        dueDate,
        dueInDays: typeof data.dueInDays === 'number' ? data.dueInDays : undefined,
        ownerId: s(data.roleId) ? `${ROLE_ASSIGNEE_PREFIX}${s(data.roleId)}` : s(data.assigneeId),
      }
    }
    case 'add_tags':
    case 'remove_tags':
      return {
        label: engineType === 'remove_tags' ? 'Remove tags' : 'Tag',
        // The engine stores the target ("contact" or the triggering activity); assuming contact
        // moved an activity's tags onto the patient record on the next save.
        target: s(data.target) === 'activity' ? 'activity' : 'contact',
        operation: engineType === 'remove_tags' ? 'remove' : 'add',
        tags: Array.isArray(data.tags) ? data.tags : [],
      }
    case 'set_pipeline_stage':
      return { label: copy.updateList, operation: 'add', pipelineId: s(data.pipelineId), stage: s(data.stage) }
    case 'assign_owner':
      return { label: 'Assign owner', ownerId: s(data.userId) }
    case 'create_note':
      return { label: 'Add note', body: s(data.content) ?? '' }
    case 'send_paperwork':
      return {
        label: 'Send paperwork',
        paperworkTemplateId: s(data.paperworkTemplateId),
        expiresInDays: typeof data.expiresInDays === 'number' ? data.expiresInDays : DEFAULT_PAPERWORK_EXPIRY_DAYS,
        message: s(data.message) ?? '',
        toField: s(data.toField) ?? 'cellPhone',
        nightSend: s(data.quietHoursAction) === 'skip' ? 'skip' : 'hold',
      }
    case 'send_notification': {
      // The engine's three values are `email`, `both` and `app`; the canvas calls the last
      // `in_app`. Collapsing anything that was not `email` to `in_app` silently downgraded a
      // notification set to BOTH, so it stopped emailing on the next save.
      const channel = s(data.channel)
      return {
        label: 'Notify',
        channel: channel === 'email' || channel === 'both' ? channel : 'in_app',
        recipients: Array.isArray(data.recipients) ? data.recipients : [],
        message: s(data.message) ?? '',
      }
    }
    case 'wait': {
      const earlyExit = parseJsonField<BranchCondition>(data.earlyExit)
      if (!earlyExit?.filters?.length) {
        return {
          label: 'Wait',
          ...decodeDuration(s(data.duration)),
          businessHoursOnly: data.businessHoursOnly === true,
        }
      }
      return {
        label: 'Wait until',
        ...decodeDuration(s(data.duration)),
        match: earlyExit.filterLogic === 'or' ? 'any' : 'all',
        conditions: earlyExit.filters.map((f) =>
          conditionFromFilter(
            f.attribute === CONTACT_TAGS_ATTRIBUTE ? CONTACT_TAGS_FIELD : stepStageAttribute(f.attribute, s(f.value)),
            f.operator,
            f.value,
          ),
        ),
      }
    }
    case 'edit_task': {
      const dueDate = s(data.dueDate)
      const hasDue = !!dueDate || typeof data.dueInDays === 'number'
      const roleId = s(data.roleId)
      const assigneeId = s(data.assigneeId)
      return {
        label: 'Edit task',
        onlyIfOpen: data.onlyIfOpen === true,
        status: s(data.status) === 'open' || s(data.status) === 'closed' ? s(data.status) : undefined,
        archived: typeof data.archived === 'boolean' ? data.archived : undefined,
        ownerId: roleId ? `${ROLE_ASSIGNEE_PREFIX}${roleId}` : assigneeId,
        dueType: hasDue ? (dueDate ? 'absolute' : 'relative') : undefined,
        dueDate,
        dueInDays: typeof data.dueInDays === 'number' ? data.dueInDays : undefined,
        title: s(data.title),
        description: s(data.description),
      }
    }
    case 'wait_for_reply': {
      // Only carry the flag when the doc says false; absent means on, for engine and builder alike.
      const stop = data.stopOnHumanTakeover === false ? { stopOnHumanTakeover: false } : {}
      const options = parseJsonField<ReplyOption[]>(data.replyOptions) ?? []
      const step = options.length ? { label: 'Wait for a keyword', options } : { label: 'Wait for reply' }
      const timeout = s(data.timeout)
      if (!timeout) return { ...step, ...stop }
      const { duration, unit } = decodeDuration(timeout)
      return { ...step, within: duration, unit, ...stop }
    }
    case 'wait_for_call_result':
      return { label: 'Wait for call result' }
    case 'confirm_appointment':
      return { label: 'Confirm appointment' }
    case 'edit_contact': {
      // `fields` is a MAP and editContact.ts writes every entry of it in one save, so read every
      // entry back. Taking only the first turned a three-field step into a one-field step on the
      // next save, deleting two live writes with nothing on screen to say so.
      const parsed = parseJsonField<Record<string, unknown>>(data.fields) ?? {}
      return {
        label: 'Edit',
        target: 'contact',
        fields: Object.entries(parsed).map(([field, value]) => ({
          field,
          operation: value === null ? 'clear' : 'set',
          value: value == null ? '' : String(value),
        })),
      }
    }
    case 'branch': {
      const parsed = parseJsonField<BranchCondition[]>(data.conditions) ?? []
      const readsContact = !parsed[0]?.source || parsed[0].source === 'contact'
      const readsPaperwork = parsed[0]?.source === 'paperwork'
      const filters = parsed[0]?.filters ?? []
      const conditions: Condition[] = filters.flatMap((f): Condition[] => {
        // Serialize writes a days-ago row as a `$now-Nd` comparison, and "more than" adds an
        // attribute_not_exists twin so never-texted contacts count as quiet; the twin folds back
        // into its row.
        if (isDaysAgoFieldKey(f.attribute)) {
          const hasComparison = filters.some(
            (other) => other.attribute === f.attribute && other.operator === 'lesser_than',
          )
          if (f.operator === 'attribute_not_exists' && hasComparison) return []
          const days = typeof f.value === 'string' ? /^\$now-(\d+)d$/.exec(f.value)?.[1] : undefined
          if (days && f.operator === 'lesser_than') {
            return [{ type: 'field', field: f.attribute, operator: 'more_than_days_ago', value: days }]
          }
          if (days && f.operator === 'greater_equal_than') {
            return [{ type: 'field', field: f.attribute, operator: 'less_than_days_ago', value: days }]
          }
        }
        // The stage lookup only ever matches strings; a boolean-field filter stores a real boolean.
        const stored = typeof f.value === 'string' ? f.value : undefined
        const field =
          readsContact && f.attribute === CONTACT_TAGS_ATTRIBUTE
            ? CONTACT_TAGS_FIELD
            : readsPaperwork && f.attribute === 'status'
              ? PAPERWORK_STATUS_FIELD
              : stepStageAttribute(f.attribute, stored)
        return [conditionFromFilter(field, f.operator, f.value)]
      })
      // Read the stored logic rather than assuming ALL. The engine keeps it as the condition's
      // `filterLogic`, and serialize writes it, but this hard-coded `all` threw it away: a branch
      // saved as "Any condition" reopened as "All conditions", and saving from that canvas rewrote
      // the stored trigger to `and`.
      //
      // For a branch testing one single-valued attribute against several alternatives — four
      // `pipelines.key is <stage>` rows, say — that inversion is not a nuance, it is unsatisfiable:
      // a card sits on exactly one stage, so every run takes the default path and the workflow
      // silently stops doing anything. That was live on Braselton Smiles Studio's hygiene exit
      // workflow, 26 runs and no card ever moved.
      //
      // Same shape as the trigger's own mapping below (`trigger.filterLogic === 'or'`).
      //
      // The label follows the condition id for the same reason `kindFromEngineNode` does: 'pass' is
      // a Filter's single gate, anything else is a Branch's fork.
      return {
        label: parsed[0]?.id === 'pass' ? 'Filter' : 'Branch',
        match: parsed[0]?.filterLogic === 'or' ? 'any' : 'all',
        conditions,
      }
    }
    default:
      return { label: engineType }
  }
}

/** Backend Workflow → local WorkflowDoc. Prefers the lossless `builderJson` blob (the exact canvas we
 *  saved); falls back to reconstructing from the engine's executable nodes when it's absent (e.g. a
 *  workflow created directly via the API). */
export function workflowDocFromRemote(remote: Workflow, copy: ListCopy): WorkflowDoc {
  const builder = parseJsonField<{
    name?: string
    triggerStartDate?: string
    backfill?: boolean
    wentLive?: boolean
    nodes?: WorkflowNode[]
    edges?: Edge[]
    settings?: WorkflowSettings
  }>(remote.builderJson)
  if (builder?.nodes?.length) {
    const builderNodes = upgradeStoredNodes(builder.nodes)
    const triggerNode = builderNodes.find((node) => node.type === 'trigger')
    const kind = (triggerNode?.data as { kind?: TriggerKind } | undefined)?.kind ?? 'contact_created'
    // A canvas written outside the builder can carry the engine's 'pass' handle on a Filter edge. The
    // card has one unnamed source handle, so React Flow would drop the edge (see `decodeSourceHandle`).
    const filterIds = new Set(builderNodes.filter((node) => node.type === 'filter').map((node) => node.id))
    const edges = (builder.edges ?? []).map((edge) =>
      filterIds.has(edge.source) && edge.sourceHandle ? { ...edge, sourceHandle: undefined } : edge,
    )
    return {
      id: remote.id,
      remoteId: remote.id,
      name: remote.name ?? builder.name ?? 'Untitled workflow',
      active: remote.active,
      wentLive: builder.wentLive,
      trigger: triggerNode ? triggerLabel(kind) : 'No trigger',
      triggerStartDate: remote.triggerStartDate,
      backfill: builder.backfill,
      // Read off the record rather than builderJson: the location is engine-owned now, and a
      // workflow created through the API has no builder blob at all.
      locationId: remote.locationId,
      nodes: builderNodes,
      edges,
      settings: builder.settings ?? DEFAULT_WORKFLOW_SETTINGS,
      updatedAt: Date.parse(remote.updatedDate) || 0,
    }
  }

  const engineNodes = parseJsonField<EngineNode[]>(remote.nodesJson) ?? []
  const engineEdges = parseJsonField<EngineEdge[]>(remote.edgesJson) ?? []
  const trigger = parseJsonField<EngineTrigger>(remote.triggerJson) ?? {}

  const kindById = new Map<string, WorkflowNodeKind>()
  const nodes: WorkflowNode[] = engineNodes.map((n) => {
    // Lower-cased for the same reason `kindFromEngine` lower-cases the event: the backend stores
    // these lower-cased but the GraphQL enum we send is upper (`BRANCH`), so a doc read straight
    // back from a serialize result would miss every entry in the map. The fallback is `wait`, so
    // that miss did not fail loudly — it turned a Branch into a silent pause with the label
    // "BRANCH". Anything still unmapped after this is a node kind the frontend genuinely does not
    // know, which is the case the fallback is actually for.
    const engineType = n.type?.toLowerCase() ?? ''
    const kind = kindFromEngineNode(engineType, n.data ?? {})
    kindById.set(n.id, kind)
    return {
      id: n.id,
      type: kind,
      position: n.position ?? { x: 0, y: 0 },
      data: nodeDataFromEngine(engineType, n.data ?? {}, copy),
    } as WorkflowNode
  })

  const edges: Edge[] = engineEdges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: decodeSourceHandle(kindById.get(e.source), e.sourceHandle),
    type: 'connector',
    animated: true,
  }))

  // The trigger node isn't stored — rebuild it and connect it to the entry node(s) (no incoming edge).
  const typeFilter = trigger.filters?.find((f) => f.attribute === 'type')?.value
  // The manual-enrollment convention (TRK-5430): a CONTACT_UPDATED trigger carrying the
  // never-match filter IS the manual kind — recognize it before the generic event mapping, and
  // keep the convention filter out of the visible condition rows.
  const isManual = isManualEnrollmentTrigger(trigger)
  const kind = isManual ? ('manual' as const) : kindFromEngine(trigger.event, typeFilter)
  // A treatment trigger's moment and treatment choices are fixed filters, not condition rows.
  const { treatment, rest: changeFilters } =
    kind === 'treatment_plan_updated'
      ? treatmentFromFilters(trigger.filters ?? [], trigger.event)
      : { treatment: undefined, rest: trigger.filters ?? [] }
  // The changeSource filter is the "Triggered by" select, not a condition row.
  const sourceFilter = trigger.filters?.find((f) => f.attribute === 'changeSource')?.value
  const source = sourceFilter === 'user' || sourceFilter === 'system' ? sourceFilter : undefined
  // Message filters store raw Message fields (direction/channel/…) — map back to our alias keys
  // (messageDirection/…) so the condition rows resolve to labelled, pickable fields. State filters
  // are serialized with a STEP's attribute mapping (they read the live record), so they reverse with
  // the same function; change filters don't, because their trigger-side mapping only ever emits
  // `pipelines.key` and reversing a `pipelines.stage` there would rewrite it on the next save.
  const fieldFor = (attribute: string, value: string | undefined, scope: ConditionScope) => {
    if (kind === 'message_created') return MESSAGE_FIELD_FROM_ENGINE[attribute] ?? attribute
    // Only a contact trigger reads the contact's tags; anywhere else `tags` is the activity's own.
    if (attribute === CONTACT_TAGS_ATTRIBUTE && triggerObject(kind) === 'contact') return CONTACT_TAGS_FIELD
    return scope === 'state' ? stepStageAttribute(attribute, value) : attribute
  }
  // Keep the raw activity type only for the generic Activity triggers; first-class Form/Call kinds
  // carry no activity type (they ride their own Event-backed sources since the pivot).
  const activityType = GENERIC_ACTIVITY_KINDS.has(kind) ? typeFilter : undefined
  // A date-anchored trigger's change filters ARE its schedule: one `date_reached` filter per moment,
  // all on the same date field (serialize's date branch). Rebuild the anchor and the moments here:
  // left to fall through, they came back as a nonsense "<dateField> is +1h" condition row.
  const { dateField, dateOffsets, invalidDateOffsets } = dateScheduleFromFilters(
    isDateAnchored(kind) ? (trigger.filters ?? []) : [],
  )
  // Any non-`type` trigger filter is a user entry condition (the `type` filter became the kind/
  // activityType). Change-filters and state-filters carry their scope explicitly so the round-trip
  // never re-derives it from field-class defaults.
  const entryConditions: Condition[] = [
    ...changeFilters
      .filter(
        (f) =>
          f.attribute !== 'type' &&
          f.attribute !== 'changeSource' &&
          f.operator !== DATE_REACHED_OPERATOR &&
          !(isManual && f.attribute === 'leadStatus' && f.value === 'manual-enrollment-only'),
      )
      .map((f) => conditionFromFilter(fieldFor(f.attribute, f.value, 'change'), f.operator, f.value, 'change')),
    ...(trigger.stateFilters ?? []).map((f) =>
      conditionFromFilter(fieldFor(f.attribute, f.value, 'state'), f.operator, f.value, 'state'),
    ),
  ]
  const triggerId = ObjectId()
  const targeted = new Set(engineEdges.map((e) => e.target))
  const entries = engineNodes.filter((n) => !targeted.has(n.id))
  const anchor = entries[0]?.position ?? { x: 250, y: 200 }
  const triggerNode = {
    id: triggerId,
    type: 'trigger' as const,
    position: { x: anchor.x, y: anchor.y - 140 },
    data: {
      label: 'Trigger',
      kind,
      activityType,
      source,
      treatment,
      dateField,
      dateOffsets: dateOffsets.length ? dateOffsets : undefined,
      invalidDateOffsets: invalidDateOffsets.length ? invalidDateOffsets : undefined,
      conditions: entryConditions.length ? entryConditions : undefined,
      match: trigger.filterLogic === 'or' ? ('any' as const) : ('all' as const),
      // ABSENT means the engine requires a match (`!== false` in enrollWorkflowEvent), so a doc
      // saved before the builder serialized the toggle explicitly must display as ON — showing off
      // while the engine skips unmatched events is the lie that hid the 08-24 contact-less no-show.
      requireContactMatch: trigger.requireContactMatch !== false,
      reenrollment: REENROLLMENT_FROM_ENGINE[trigger.reenrollment ?? 'ignore'] ?? 'ignore',
    },
  } as WorkflowNode
  nodes.unshift(triggerNode)
  for (const entry of entries) {
    edges.push({ id: ObjectId(), source: triggerId, target: entry.id, type: 'connector', animated: true })
  }

  return {
    id: remote.id,
    remoteId: remote.id,
    name: remote.name,
    active: remote.active,
    trigger: triggerLabel(kind),
    triggerStartDate: remote.triggerStartDate,
    nodes,
    edges,
    settings: DEFAULT_WORKFLOW_SETTINGS,
    locationId: remote.locationId,
    updatedAt: Date.parse(remote.updatedDate) || 0,
  }
}
